import { z } from "zod";

import { CrmDealCreationResultSchema } from "./crmDealCreation";

/**
 * Opportunity, lead and conversation-sync contracts. They live beside `salesforce.ts` rather
 * than in it because that file is already near the 500-line cap — the same split
 * `dynamics.ts` needed.
 */

// ── Pipelines (= Opportunity record types) ───────────────────────────

/**
 * Which id space a pipeline's `id` belongs to.
 *
 * Salesforce has no pipeline object. What the picker calls a pipeline is an Opportunity
 * **record type**, whose id selects the Sales Process and therefore the allowed stages. Orgs
 * frequently have none, in which case the backend synthesises exactly one pipeline over the
 * whole active `StageName` picklist.
 *
 * The two spaces are **not interchangeable on write** — a record-type id goes into
 * `RecordTypeId`, the synthesised id is never written at all — so the kind travels on the DTO
 * instead of being re-derived from the id's shape by every consumer.
 */
export enum SalesforcePipelineKind {
	RECORD_TYPE = "RECORD_TYPE",
	DEFAULT = "DEFAULT",
}

export const SalesforcePipelineStageSchema = z.object({
	id: z.string(),
	label: z.string(),
	displayOrder: z.number().optional(),
});

export type SalesforcePipelineStage = z.infer<typeof SalesforcePipelineStageSchema>;

export const SalesforcePipelineSchema = z.object({
	id: z.string(),
	label: z.string(),
	pipelineKind: z.nativeEnum(SalesforcePipelineKind),
	stages: z.array(SalesforcePipelineStageSchema),
});

export type SalesforcePipeline = z.infer<typeof SalesforcePipelineSchema>;

export const SalesforcePipelinesResponseSchema = z.object({
	pipelines: z.array(SalesforcePipelineSchema),
});

export type SalesforcePipelinesResponse = z.infer<typeof SalesforcePipelinesResponseSchema>;

// ── Opportunity-creation trigger ─────────────────────────────────────

export enum SalesforceDealCreationTrigger {
	DISABLED = "DISABLED",
	ON_RESPONSE = "ON_RESPONSE",
	ON_POSITIVE_RESPONSE = "ON_POSITIVE_RESPONSE",
}

export const SalesforceDealCreationTriggerResponseSchema = z.object({
	trigger: z.nativeEnum(SalesforceDealCreationTrigger),
	createAsLead: z.boolean(),
});

export type SalesforceDealCreationTriggerResponse = z.infer<
	typeof SalesforceDealCreationTriggerResponseSchema
>;

export const UpdateSalesforceDealCreationTriggerSchema = z.object({
	trigger: z.nativeEnum(SalesforceDealCreationTrigger),
	createAsLead: z.boolean().optional(),
});

export type UpdateSalesforceDealCreationTrigger = z.infer<
	typeof UpdateSalesforceDealCreationTriggerSchema
>;

// ── Opportunity configuration ────────────────────────────────────────

/**
 * The column names, not the Dynamics ones. Ticket 1 created `closeDateOffsetDays` and
 * `defaultAmount`; §4.4 and §4.6 of the plan still call them `expectedCloseDateOffset` and
 * `estimatedValue`, and the DTOs follow the columns (§4.4's ticket-6 corrections).
 */
export const SalesforceDealConfigResponseSchema = z.object({
	defaultPipelineId: z.string().nullish(),
	pipelineKind: z.nativeEnum(SalesforcePipelineKind).nullish(),
	defaultStageId: z.string().nullish(),
	dealNameTemplate: z.string().nullish(),
	closeDateOffsetDays: z.coerce.number().nullish(),
	defaultOwnerId: z.string().nullish(),
	defaultAmount: z.coerce.number().nullish(),
});

export type SalesforceDealConfigResponse = z.infer<typeof SalesforceDealConfigResponseSchema>;

export const UpdateSalesforceDealConfigSchema = z.object({
	defaultPipelineId: z.string().optional(),
	defaultStageId: z.string().optional(),
	dealNameTemplate: z.string().optional(),
	closeDateOffsetDays: z.number().optional(),
	defaultOwnerId: z.string().optional(),
	defaultAmount: z.number().optional(),
});

export type UpdateSalesforceDealConfig = z.infer<typeof UpdateSalesforceDealConfigSchema>;

// ── Lead configuration ───────────────────────────────────────────────

/**
 * `estimatedValue` round-trips but is **not written**: a standard Salesforce `Lead` has no
 * currency field at all (§4.4's ticket-6 corrections). `defaultLeadStatus` is real and is
 * written to `Lead.Status` as a *default* — a LEAD mapping onto `Status` still wins.
 */
export const SalesforceLeadConfigResponseSchema = z.object({
	leadTopicTemplate: z.string().nullish(),
	defaultOwnerId: z.string().nullish(),
	estimatedValue: z.coerce.number().nullish(),
	defaultLeadStatus: z.string().nullish(),
});

export type SalesforceLeadConfigResponse = z.infer<typeof SalesforceLeadConfigResponseSchema>;

export const UpdateSalesforceLeadConfigSchema = z.object({
	leadTopicTemplate: z.string().optional(),
	defaultOwnerId: z.string().optional(),
	estimatedValue: z.number().optional(),
	defaultLeadStatus: z.string().optional(),
});

export type UpdateSalesforceLeadConfig = z.infer<typeof UpdateSalesforceLeadConfigSchema>;

// ── Conversation sync ────────────────────────────────────────────────

export const SalesforceConversationSyncResponseSchema = z.object({
	enabled: z.boolean(),
});

export type SalesforceConversationSyncResponse = z.infer<
	typeof SalesforceConversationSyncResponseSchema
>;

export const UpdateSalesforceConversationSyncSchema = z.object({
	enabled: z.boolean(),
});

export type UpdateSalesforceConversationSync = z.infer<
	typeof UpdateSalesforceConversationSyncSchema
>;

// ── Manual opportunity / lead creation ───────────────────────────────

export const CreateSalesforceDealRequestSchema = z.object({
	prospectId: z.number(),
	dealName: z.string().optional(),
	pipeline: z.string().optional(),
	dealStage: z.string().optional(),
	dealOwnerId: z.string().optional(),
	closeDate: z.string().optional(),
	amount: z.number().optional(),
	createAsLead: z.boolean().optional(),
});

export type CreateSalesforceDealRequest = z.infer<typeof CreateSalesforceDealRequestSchema>;

export const CreateSalesforceDealResponseSchema = CrmDealCreationResultSchema;

export type CreateSalesforceDealResponse = z.infer<typeof CreateSalesforceDealResponseSchema>;
